import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MuiButton from '@mui/material/Button';
import CheckIcon from '@mui/icons-material/Check';
import API from 'src/api/config';
import { track } from 'src/utils/analytics';

const SHADOW = '4px 4px 0px #1e1e1e';

interface PricingTier {
  name: string;
  badge?: string;
  price: string;
  priceNote?: string;
  audience: string;
  features: string[];
  cta: string;
  ctaPath: string;
  checkoutPlan?: string;
  highlighted?: boolean;
}

const TIERS: PricingTier[] = [
  {
    name: 'Classroom',
    badge: 'Most Popular',
    price: '$99',
    priceNote: '/year',
    audience: 'For individual teachers',
    features: [
      'Unlimited students',
      'Unlimited published newspapers',
      'AI Writing Buddy',
      'Templates & writing scaffolds',
      'PDF export & pressto.fun sharing',
      'School branding on newspapers',
    ],
    cta: 'Start 7-Day Free Trial',
    ctaPath: '/register',
    checkoutPlan: 'classroom',
    highlighted: true,
  },
  {
    name: 'School',
    badge: 'Best Value',
    price: '$999',
    priceNote: '/year',
    audience: 'For all teachers at one school',
    features: [
      'Everything in Classroom plan',
      'Unlimited teachers',
      'School-wide newspaper',
      'Admin dashboard',
      'Trophy case & newspaper archive',
      'Priority support',
    ],
    cta: 'Get Started',
    ctaPath: '/contact',
    checkoutPlan: 'school',
  },
  {
    name: 'District',
    badge: 'Enterprise',
    price: 'Custom',
    audience: 'For multiple schools',
    features: [
      'Everything in School plan',
      'Unlimited schools',
      'District-level oversight',
      'Dedicated support',
      'Custom onboarding',
      'Volume pricing',
    ],
    cta: 'Get a Quote',
    ctaPath: '/contact',
  },
];

export default function PricingCards() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<string | null>(null);

  const handleClick = async (tier: PricingTier) => {
    track('pricing_cta_clicked', {
      tier: tier.name,
      plan: tier.checkoutPlan ?? null,
      leads_to_stripe: !!tier.checkoutPlan,
    });
    if (tier.checkoutPlan) {
      setLoading(tier.name);
      try {
        const { data } = await API.post('/checkout/create-session', { plan: tier.checkoutPlan });
        track('checkout_session_started', { tier: tier.name, plan: tier.checkoutPlan });
        window.location.href = data.url;
      } catch {
        track('checkout_session_failed', { tier: tier.name, plan: tier.checkoutPlan });
        setLoading(null);
        navigate(tier.ctaPath);
      }
    } else {
      navigate(tier.ctaPath);
    }
  };

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
        gap: 3,
        maxWidth: 1100,
        mx: 'auto',
      }}
    >
      {TIERS.map((tier) => (
        <Box
          key={tier.name}
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: '6px',
            border: '2px solid #1e1e1e',
            boxShadow: tier.highlighted ? '6px 6px 0px #1e1e1e' : SHADOW,
            backgroundColor: '#FFFFFF',
            position: 'relative',
          }}
        >
          {tier.badge && (
            <Box
              sx={{
                position: 'absolute',
                top: -14,
                left: 20,
                backgroundColor: tier.highlighted ? '#FFE082' : '#F7F7F7',
                color: '#2C2C2C',
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '0.7rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                px: 2,
                py: 0.5,
                border: '2px solid #1e1e1e',
                borderRadius: '4px',
              }}
            >
              {tier.badge}
            </Box>
          )}

          <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '1.2rem', color: '#2C2C2C', mb: 0.5 }}>
            {tier.name}
          </Typography>
          <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.85rem', color: '#888', mb: 3 }}>
            {tier.audience}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 3 }}>
            <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontSize: '2.5rem', fontWeight: 800, color: '#2C2C2C' }}>
              {tier.price}
            </Typography>
            {tier.priceNote && (
              <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.9rem', color: '#888', ml: 0.5 }}>
                {tier.priceNote}
              </Typography>
            )}
          </Box>

          <Box sx={{ mb: 4 }}>
            {tier.features.map((feature) => (
              <Box key={feature} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
                <CheckIcon sx={{ fontSize: '1rem', color: '#2C2C2C', mt: 0.25, flexShrink: 0 }} />
                <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: '0.875rem', color: '#444', lineHeight: 1.5 }}>
                  {feature}
                </Typography>
              </Box>
            ))}
          </Box>

          <MuiButton
            fullWidth
            variant="contained"
            disabled={loading === tier.name}
            onClick={() => handleClick(tier)}
            sx={{
              backgroundColor: tier.highlighted ? '#2C2C2C' : '#FFFFFF',
              color: tier.highlighted ? '#FFFFFF' : '#2C2C2C',
              border: '2px solid #1e1e1e',
              boxShadow: '3px 3px 0px #1e1e1e',
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 700,
              fontSize: '0.9rem',
              textTransform: 'none',
              borderRadius: '6px',
              py: 1.5,
              '&:hover': {
                backgroundColor: tier.highlighted ? '#1e1e1e' : '#F7F7F7',
                boxShadow: '1px 1px 0px #1e1e1e',
                transform: 'translate(2px, 2px)',
              },
              transition: 'all 100ms ease-in-out',
            }}
          >
            {loading === tier.name ? 'Loading...' : tier.cta}
          </MuiButton>
        </Box>
      ))}
    </Box>
  );
}
